import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all duration-200 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-neutral-900 text-white hover:bg-neutral-800 border border-neutral-900",
        destructive:
          "bg-white text-red-600 border border-red-600 hover:bg-red-50",
        outline:
          "border border-neutral-300 bg-white hover:bg-neutral-50 hover:border-neutral-400",
        secondary:
          "bg-neutral-100 text-neutral-900 border border-neutral-200 hover:bg-neutral-200 hover:border-neutral-300",
        ghost: "hover:bg-neutral-100 hover:text-neutral-900",
        link: "text-neutral-900 underline-offset-4 hover:underline",
        accent:
          "bg-neutral-800 text-white border border-neutral-800 hover:bg-neutral-700",
      },
      size: {
        default: "h-11 px-5 py-2 rounded-none",
        sm: "h-9 px-3 text-xs rounded-none",
        lg: "h-12 px-6 text-base rounded-none",
        xl: "h-14 px-8 text-lg rounded-none",
        icon: "h-11 w-11 rounded-none",
        "icon-sm": "h-9 w-9 rounded-none",
        "icon-lg": "h-12 w-12 rounded-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Required for icon-only buttons to meet accessibility requirements */
  ariaLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      type = "button",
      ariaLabel,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Detect if this is an icon-only button (size contains 'icon')
    const isIconOnly = size?.includes("icon");

    // Warn in development if icon-only button lacks aria-label
    if (process.env.NODE_ENV === "development" && isIconOnly && !ariaLabel) {
      console.warn(
        "[Accessibility] Icon-only buttons must have an aria-label prop for screen reader users.",
      );
    }

    return (
      <Comp
        type={type}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
